// src/pages/school/SchoolAdminDashboard.js  (PART 1/2)
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row, Col, Card, Badge, Button, Form, Offcanvas, Placeholder, Spinner,
} from "react-bootstrap";
import dayjs from "dayjs";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as VList } from "react-window";
import { motion, AnimatePresence } from "framer-motion";
import useSchool from "../../hooks/useSchool";
import {
  collection, query, where, orderBy, limit as qLimit,
  onSnapshot, getCountFromServer, getDocs
} from "firebase/firestore";
import { db } from "../../services/firebase";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
  AreaChart, Area
} from "recharts";

// Storage
import { storage } from "../../services/firebase";
import { getDownloadURL, ref as storageRef, listAll } from "firebase/storage";

/* ─────────────── 3D PNG Icons ─────────────── */
import PNG_Profile     from "../../assets/3d/profile.png";
import PNG_Classes     from "../../assets/3d/online-class.png";
import PNG_Assignments from "../../assets/3d/assignments.png";
import PNG_Progress    from "../../assets/3d/progress.png";     // student results
import PNG_Exams       from "../../assets/3d/exams.png";
import PNG_Fees        from "../../assets/3d/fees.png";
import PNG_Attendance  from "../../assets/3d/attendance.png";
import PNG_Megaphone   from "../../assets/3d/megaphone.png";
import PNG_Dashboard   from "../../assets/3d/dashboard.png";
import PNG_Teachers    from "../../assets/3d/teacher.png";
import PNG_Students    from "../../assets/3d/roster.png";
import PNG_Support     from "../../assets/3d/support.png";
import PNG_Settings    from "../../assets/3d/settings.png";

/* =========================================================================
   Helpers
   ========================================================================= */
const toDate = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === "function") { try { return v.toDate(); } catch {} }
  if (typeof v === "object" && typeof v.seconds === "number") {
    const d = new Date(v.seconds * 1000);
    return isNaN(d) ? null : d;
  }
  if (v instanceof Date) return isNaN(v) ? null : v;
  const d = new Date(v);
  return isNaN(d) ? null : d;
};
const fmtDate = (d, f = "DD MMM YYYY") =>
  d && dayjs(d).isValid() ? dayjs(d).format(f) : "—";
const daysLeft = (dt) => {
  if (!dt) return null;
  const d = dayjs(dt); if (!d.isValid()) return null;
  const diff = d.diff(dayjs(), "day"); return diff >= 0 ? diff : 0;
};
const buildLastNDays = (n = 14) => {
  const days = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = dayjs().startOf("day").subtract(i, "day");
    days.push({ key: d.format("YYYY-MM-DD"), label: d.format("DD MMM") });
  }
  return days;
};
const bumpCount = (map, key) => map.set(key, (map.get(key) || 0) + 1);

/* =========================================================================
   Theme + Accent
   ========================================================================= */
function useThemeVars(deps = []) {
  const [vars, setVars] = useState({
    primary: "#0d6efd", success: "#198754", warning: "#ffc107",
    info: "#0dcaf0", danger: "#dc3545", secondary: "#6c757d",
    bodyBg: "#ffffff", bodyColor: "#212529",
    border: "#dee2e6", grid: "#e9ecef", muted: "#6c757d",
    accent: "#0d6efd"
  });
  useEffect(() => {
    const cs = getComputedStyle(document.documentElement);
    const get = (v) => cs.getPropertyValue(v).trim();
    setVars((p) => ({
      ...p,
      primary: get("--bs-primary") || "#0d6efd",
      success: get("--bs-success") || "#198754",
      warning: get("--bs-warning") || "#ffc107",
      info: get("--bs-info") || "#0dcaf0",
      danger: get("--bs-danger") || "#dc3545",
      secondary: get("--bs-secondary") || "#6c757d",
      bodyBg: get("--bs-body-bg") || "#ffffff",
      bodyColor: get("--bs-body-color") || "#212529",
      border: get("--bs-border-color") || "#dee2e6",
      grid: get("--bs-tertiary-bg") || "#e9ecef",
      muted: get("--bs-secondary-color") || "#6c757d",
      accent: get("--accent") || (localStorage.getItem("accentColor") || "#0d6efd"),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return vars;
}
const hexToRGB = (hex) => {
  const s = String(hex || "").replace("#", "");
  const nn = s.length === 3 ? s.split("").map((x)=>x+x).join("") : s;
  const n = parseInt(nn || "0d6efd", 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const withAlpha = (hex, a) => {
  const { r, g, b } = hexToRGB(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
function useAccent(vars) {
  const [accent, setAccent] = useState(() => localStorage.getItem("accentColor") || vars.primary);
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty("--accent", accent);
    root.setProperty("--accent-04", withAlpha(accent, 0.04));
    root.setProperty("--accent-10", withAlpha(accent, 0.10));
    root.setProperty("--accent-18", withAlpha(accent, 0.18));
    root.setProperty("--accent-30", withAlpha(accent, 0.30));
    localStorage.setItem("accentColor", accent);
  }, [accent]);
  return [accent, setAccent];
}

/* =========================================================================
   Micro components (with 3D icon support)
   ========================================================================= */
/** Safe 3D PNG renderer (theme-aware drop shadow, perfect fit) */
function Icon3D({ src, size = 40, alt = "", className = "", tilt = true, style }) {
  return (
    <motion.img
      src={src}
      alt={alt}
      draggable={false}
      className={`icon3d ${className}`}
      style={{
        width: size,
        height: size,
        aspectRatio: "1 / 1",
        objectFit: "contain",
        borderRadius: 12,
        ...style,
      }}
      initial={tilt ? { y: 0, rotate: 0 } : false}
      whileHover={
        tilt ? { y: -2, rotate: 2, transition: { type: "spring", stiffness: 280, damping: 18 } } : undefined
      }
    />
  );
}

const GlowTile = ({ title, value, hint, icon, png, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 8, scale: 0.98 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.45, delay: delay / 1000, ease: [0.2, 0.8, 0.2, 1] }}
  >
    <Card className="border-0 h-100 glow-tile hover-lift">
      <Card.Body className="d-flex justify-content-between align-items-start">
        <div>
          <div className="text-muted small">{title}</div>
          <div className="fs-3 fw-semibold">
            {typeof value === "number" ? <CountUp to={value} /> : value}
          </div>
          {hint && <div className="text-muted small mt-1">{hint}</div>}
        </div>
        <motion.div whileHover={{ rotate: 4, scale: 1.05 }} transition={{ type: "spring", stiffness: 260, damping: 16 }}>
          <div className="tile-icon">
            {png ? <Icon3D src={png} size={40} alt={`${title} icon`} /> : icon}
          </div>
        </motion.div>
      </Card.Body>
    </Card>
  </motion.div>
);

const SkeletonCard = ({ lines = 3 }) => (
  <Card className="shadow-sm border-0 h-100">
    <Card.Body>
      <Placeholder as="div" animation="glow">
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="mb-2">
            <Placeholder xs={i === 0 ? 8 : 5} />{i === 0 ? <><br /><Placeholder xs={4} /></> : null}
          </div>
        ))}
      </Placeholder>
    </Card.Body>
  </Card>
);

/** Count-up number animation */
function CountUp({ to = 0, dur = 800 }) {
  const ref = useRef(null);
  const raf = useRef(0);
  useEffect(() => {
    const t0 = performance.now();
    const from = 0;
    const animate = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = Math.round(from + (to - from) * eased);
      if (ref.current) ref.current.textContent = val.toLocaleString();
      if (p < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [to, dur]);
  return <span ref={ref}>0</span>;
}

/* =========================================================================
   Virtualized list
   ========================================================================= */
const rowMotion = {
  initial: { opacity: 0, x: -6 },
  animate: { opacity: 1, x: 0, transition: { duration: .25 } },
  whileHover: { x: 4, transition: { type: "spring", stiffness: 200, damping: 18 } }
};
function VirtualList({ items, rowHeight = 56, renderRow, emptyText = "No data" }) {
  if (!items?.length) {
    return <div className="text-center text-muted py-4">{emptyText}</div>;
  }
  const RowCell = ({ index, style }) => (
    <motion.div
      style={{ ...style, display: "flex", alignItems: "center", padding: "0 .75rem" }}
      variants={rowMotion} initial="initial" animate="animate" whileHover="whileHover"
    >
      {renderRow(items[index])}
    </motion.div>
  );
  return (
    <div style={{ height: 360 }}>
      <AutoSizer>
        {({ width, height }) => (
          <VList itemCount={items.length} height={height} width={width} itemSize={rowHeight}>
            {RowCell}
          </VList>
        )}
      </AutoSizer>
    </div>
  );
}

/* =========================================================================
   Pinned section (sticky)
   ========================================================================= */
function PinSection({ children, height = 620, offsetTop = 72 }) {
  return (
    <div style={{ height }}>
      <div className="pin-shell">{children}</div>
      <style>{`
        .pin-shell { position: sticky; top: ${offsetTop}px; will-change: transform; transform: translateZ(0); }
      `}</style>
    </div>
  );
}

/* =========================================================================
   Storage helpers: resolve school logo from gs://photos-oysi5/schools/{id}/branding
   ========================================================================= */
const BRAND_BUCKET_URL = "gs://photos-oysi5";

async function tryGetURL(path) {
  try {
    const url = await getDownloadURL(storageRef(storage, path));
    return url || null;
  } catch {
    return null;
  }
}

async function resolveSchoolLogoURL(school) {
  if (!school?.id) return null;

  const fromField = school.logoUrl || school.logoURL;
  if (fromField) return fromField;

  const base = `${BRAND_BUCKET_URL}/schools/${school.id}/branding`;
  const candidates = [
    "logo.png","logo.jpg","logo.jpeg","logo.webp","logo.svg",
    "brand.png","brand.jpg","brand.jpeg","brand.webp","brand.svg",
    "emblem.png","emblem.jpg","emblem.jpeg","emblem.webp","emblem.svg",
  ];
  for (const name of candidates) {
    const u = await tryGetURL(`${base}/${name}`);
    if (u) return u;
  }

  try {
    const res = await listAll(storageRef(storage, base));
    const items = res.items || [];
    if (!items.length) return null;

    const imageExt = /\.(png|jpg|jpeg|webp|svg)$/i;
    const prefer = (x) => /logo|brand|emblem/i.test(x.name) && imageExt.test(x.name);
    const ok = items.filter((x) => imageExt.test(x.name));

    const best = ok.find(prefer) || ok[0] || items[0];
    if (!best) return null;
    return await getDownloadURL(best);
  } catch {
    return null;
  }
}

/* clamp helper -> returns css clamp() string (min px, vw, max px) */
function clampWidth(idealPx = 120, vw = 18, maxPx = 200, minPx = 96) {
  return `clamp(${minPx}px, ${vw}vw, ${maxPx}px)`;
}
// src/pages/school/SchoolAdminDashboard.js  (PART 2/2)

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function SchoolAdminDashboard() {
  const { school, loading } = useSchool();

  const vars = useThemeVars([]);
  const [accent] = useAccent(vars);

  // Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([{ id: 1, who: "other", text: "Hi! How can we help today?" }]);
  const [draftMsg, setDraftMsg] = useState("");
  const sendMsg = () => {
    const t = draftMsg.trim();
    if (!t) return;
    setMessages((m) => [...m, { id: Date.now(), who: "me", text: t }]);
    setDraftMsg("");
    setTimeout(() => {
      setMessages((m) => [...m, { id: Date.now()+1, who: "other", text: "Thanks! We’ll get back shortly." }]);
    }, 600);
  };

  /* principal (robust + low read) */
  const [principal, setPrincipal] = useState({ name: null, phone: null, email: null });
  useEffect(() => {
    if (!school?.id) return;
    let unsubs = [];

    const apply = (t) => {
      if (!t) return;
      setPrincipal({
        name: (t.name || t.fullName || t.displayName || t.principal || "").trim() || "Principal",
        phone: (t.phone || t.mobile || t.principalPhone || "").trim() || null,
        email: (t.email || t.principalEmail || "").trim() || null,
      });
    };

    const directName =
      school?.principal?.name ||
      (typeof school?.principal === "string" && school.principal) ||
      school?.principalName ||
      school?.contact?.principal?.name ||
      (typeof school?.contact?.principal === "string" && school.contact.principal) ||
      school?.headmaster || school?.headMaster || null;

    if (directName) {
      apply({
        name: directName,
        phone: school?.principal?.phone || school?.principalPhone || school?.contact?.principal?.phone,
        email: school?.principal?.email || school?.principalEmail || school?.contact?.principal?.email,
      });
      return;
    }

    const colRef = collection(db, "schools", school.id, "teachers");
    const qStrongRole = query(colRef, where("role", "in", ["principal", "Principal"]), qLimit(1));
    const qStrongDesg = query(colRef, where("designation", "in", ["Principal", "Headmaster", "Headmistress"]), qLimit(1));
    const qFlag = query(colRef, where("isPrincipal", "==", true), qLimit(1));

    const handleSnap = (snap) => {
      if (!snap.empty) {
        apply(snap.docs[0].data());
        unsubs.forEach(u => u && u()); unsubs = [];
      }
    };

    unsubs.push(onSnapshot(qFlag, handleSnap));
    unsubs.push(onSnapshot(qStrongRole, handleSnap));
    unsubs.push(onSnapshot(qStrongDesg, handleSnap));

    (async () => {
      try {
        const snap = await getDocs(colRef);
        if (snap.empty) return;
        const m = snap.docs
          .map(d => d.data())
          .find(t => /principal|head\s*master|head\s*mistress/i.test(
            [t.designation, t.role, t.title, t.position, t.jobTitle].filter(Boolean).join(" ")
          ));
        if (m) apply(m);
      } catch {}
    })();

    return () => unsubs.forEach(u => u && u());
  }, [school?.id]);

  /* counts */
  const [counts, setCounts] = useState({ students: 0, teachers: 0, onlineClasses: 0, exams: 0, assignments: 0, circulars: 0 });
  useEffect(() => {
    let alive = true;
    async function fetchCounts() {
      if (!school?.id) return;
      const col = (name) => collection(db, "schools", school.id, name);
      try {
        const [cStudents, cTeachers, cOnline, cExams, cAssignments, cCirculars] = await Promise.all([
          getCountFromServer(query(col("students"))),
          getCountFromServer(query(col("teachers"))),
          getCountFromServer(query(col("onlineClasses"))),
          getCountFromServer(query(col("exams"))),
          getCountFromServer(query(col("assignments"))),
          getCountFromServer(query(col("circulars"))),
        ]);
        if (!alive) return;
        setCounts({
          students: cStudents.data().count || 0,
          teachers: cTeachers.data().count || 0,
          onlineClasses: cOnline.data().count || 0,
          exams: cExams.data().count || 0,
          assignments: cAssignments.data().count || 0,
          circulars: cCirculars.data().count || 0,
        });
      } catch {}
    }
    fetchCounts();
    return () => { alive = false; };
  }, [school?.id]);

  /* recents (live) */
  const [recent, setRecent] = useState({ circulars: [], exams: [], assignments: [] });
  useEffect(() => {
    if (!school?.id) return;
    const unsubs = [];
    const col = (name) => collection(db, "schools", school.id, name);
    const todayStart = dayjs().startOf("day").toDate();

    unsubs.push(onSnapshot(
      query(col("circulars"), orderBy("publishAt", "desc"), qLimit(1000)),
      (snap) => setRecent((r) => ({ ...r, circulars: snap.docs.map((d)=>({id:d.id, ...d.data()})) }))
    ));
    unsubs.push(onSnapshot(
      query(col("exams"), where("date", ">=", todayStart), orderBy("date", "asc"), qLimit(1000)),
      (snap) => setRecent((r) => ({ ...r, exams: snap.docs.map((d)=>({id:d.id, ...d.data()})) }))
    ));
    unsubs.push(onSnapshot(
      query(col("assignments"), where("dueAt", ">=", todayStart), orderBy("dueAt", "asc"), qLimit(1000)),
      (snap) => setRecent((r) => ({ ...r, assignments: snap.docs.map((d)=>({id:d.id, ...d.data()})) }))
    ));
  return () => unsubs.forEach((u) => u && u());
  }, [school?.id]);

  /* charts */
  const [studentsByClass, setStudentsByClass] = useState([]);
  useEffect(() => {
    if (!school?.id) return;
    const unsub = onSnapshot(collection(db, "schools", school.id, "students"), (snap) => {
      const counts = new Map();
      snap.docs.forEach((d) => bumpCount(counts, (d.data()?.className || "Unassigned").toString()));
      const arr = Array.from(counts.entries()).map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value).slice(0, 12);
      setStudentsByClass(arr);
    });
    return () => unsub();
  }, [school?.id]);

  const [activity, setActivity] = useState([]);
  const [activityFocus, setActivityFocus] = useState("all");
  useEffect(() => {
    if (!school?.id) return;
    const days = buildLastNDays(14);
    const baseMap = new Map(days.map((d) => [d.key, { date: d.label, circulars: 0, exams: 0, assignments: 0 }]));
    const bucketByDay = (jsDate) => (jsDate ? dayjs(jsDate).format("YYYY-MM-DD") : null);
    const col = (name) => collection(db, "schools", school.id, name);
    const since = dayjs().startOf("day").subtract(13, "day").toDate();

    let cDone = false, eDone = false, aDone = false;
    const finalize = () => {
      if (cDone && eDone && aDone) setActivity(Array.from(baseMap.values()).map((v) => ({ ...v })));
    };
    const unsubC = onSnapshot(
      query(col("circulars"), where("publishAt", ">=", since), orderBy("publishAt", "asc")),
      (snap) => { snap.docs.forEach((d) => {
        const k = bucketByDay(toDate(d.data()?.publishAt));
        if (k && baseMap.has(k)) { const cur = baseMap.get(k); baseMap.set(k, { ...cur, circulars: (cur.circulars || 0) + 1 }); }
      }); cDone = true; finalize(); }
    );
    const unsubE = onSnapshot(
      query(col("exams"), where("date", ">=", since), orderBy("date", "asc")),
      (snap) => { snap.docs.forEach((d) => {
        const k = bucketByDay(toDate(d.data()?.date));
        if (k && baseMap.has(k)) { const cur = baseMap.get(k); baseMap.set(k, { ...cur, exams: (cur.exams || 0) + 1 }); }
      }); eDone = true; finalize(); }
    );
    const unsubA = onSnapshot(
      query(col("assignments"), where("dueAt", ">=", since), orderBy("dueAt", "asc")),
      (snap) => { snap.docs.forEach((d) => {
        const k = bucketByDay(toDate(d.data()?.dueAt));
        if (k && baseMap.has(k)) { const cur = baseMap.get(k); baseMap.set(k, { ...cur, assignments: (cur.assignments || 0) + 1 }); }
      }); aDone = true; finalize(); }
    );
    return () => { unsubC(); unsubE(); unsubA(); };
  }, [school?.id]);

  const filterKeys = useMemo(() => {
    switch (activityFocus) {
      case "circulars": return ["circulars"];
      case "exams": return ["exams"];
      case "assignments": return ["assignments"];
      default: return ["circulars", "exams", "assignments"];
    }
  }, [activityFocus]);

  /* Resolve and cache school logo */
  const [logoURL, setLogoURL] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!school?.id) return;

      const cacheKey = `ls:logo:${school.id}`;
      const fromField = school.logoUrl || school.logoURL;
      if (fromField) {
        if (alive) setLogoURL(fromField);
        try { localStorage.setItem(cacheKey, fromField); } catch {}
        return;
      }
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) { if (alive) setLogoURL(cached); }
      } catch {}
      const url = await resolveSchoolLogoURL(school);
      if (!alive) return;
      if (url) {
        setLogoURL(url);
        try { localStorage.setItem(cacheKey, url); } catch {}
      } else {
        setLogoURL(null);
      }
    })();
    return () => { alive = false; };
  }, [school?.id, school?.logoUrl, school?.logoURL]);

  /* Loading / empty */
  if (loading) {
    return (
      <>
        <Card className="shadow-sm border-0 mb-3" style={{ overflow: "hidden" }}>
          <div style={{ height: 160, background: "linear-gradient(135deg, rgba(13,110,253,.12), rgba(102,16,242,.10))" }} />
          <Card.Body className="py-3">
            <Placeholder as="div" animation="glow">
              <Placeholder xs={3} /> <br />
              <Placeholder xs={2} /> <br />
              <div className="mt-2"><Spinner animation="border" size="sm" /></div>
            </Placeholder>
          </Card.Body>
        </Card>
        <Row className="g-3">
          <Col md={4}><SkeletonCard /></Col>
          <Col md={4}><SkeletonCard /></Col>
          <Col md={4}><SkeletonCard /></Col>
        </Row>
      </>
    );
  }
  if (!school) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="d-flex flex-column align-items-center text-center py-5">
          <div className="display-6">🏫</div>
          <h5 className="mt-2 mb-1">No school linked</h5>
          <div className="text-muted">This account isn’t connected to a school yet.</div>
        </Card.Body>
      </Card>
    );
  }

  // Logo node
  const logoNode = logoURL ? (
    <img
      src={logoURL}
      alt={`${school.name || "School"} logo`}
      className="anim-pop"
      style={{
        width: 72, height: 72, borderRadius: "20px", objectFit: "cover",
        border: `1px solid ${withAlpha(vars.bodyColor, 0.12)}`
      }}
    />
  ) : (
    <div
      className="anim-pop"
      style={{
        width: 72, height: 72, borderRadius: "20px",
        background: "var(--accent-10)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 28, fontWeight: 700, color: accent,
        border: `1px solid ${withAlpha(vars.bodyColor, 0.12)}`
      }}
    >
      {school.name ? school.name.charAt(0).toUpperCase() : "S"}
    </div>
  );

  const planLabel = (school.plan || "free").toString().toUpperCase();
  const addressLine = [school.addressLine1, school.addressLine2, school.city, school.state].filter(Boolean).join(", ");
  const sub = school.subscription || {};
  const renewal = toDate(sub.renewalDate);
  const left = daysLeft(renewal);

  /* ====== HEADER / RIBBON ====== */
  return (
    <>
      {/* HERO / RIBBON (dark/light + perfect 3D fit) */}
      <div className="ribbon hero-banner">
        <div className="hero-bg">
          <motion.span className="bubble b1" animate={{ y: [0, -6, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />
          <motion.span className="bubble b2" animate={{ y: [0, -8, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: .2 }} />
          <motion.span className="bubble b3" animate={{ y: [0, -10, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: .4 }} />
        </div>

        {/* main content */}
        <div className="hero-content">
          <div className="left d-flex align-items-start gap-3">
            {logoNode}
            <div className="flex-grow-1">
              <div className="small hero-date">{dayjs().format("MMMM D,  YYYY")}</div>
              <div className="display-6 fw-bold mb-1 hero-greet">
                Welcome back, {(principal?.name || school?.name || "Admin").toString().split(" ")[0]}!
              </div>
              <div className="hero-sub">Always stay updated in your student portal</div>

              {/* chips: Plan / Status / Renews / Days left */}
              <div className="chip-row mt-3 d-flex flex-wrap gap-2">
                <span className="chip">Plan: {planLabel}</span>
                <span className="chip">Status: {(sub.status || "trial").toString().toUpperCase()}</span>
                <span className="chip">Renews: {fmtDate(renewal)}</span>
                <span className="chip">Days left: {left ?? "—"}</span>
              </div>
            </div>
          </div>

          <div className="right d-none d-md-flex align-items-center">
            <div className="text-end hero-meta">
              <div className="fw-semibold">{school.name || "Your School"}</div>
              <div>Code: {school.code || "—"}</div>
              <div className="mt-1 small d-flex align-items-center gap-1">
                <i className="bi bi-geo-alt" /> {addressLine || "Address not set"}
              </div>
            </div>
          </div>
        </div>

        {/* principal + quick actions with 3D icons */}
        <div className="principal-strip soft">
          <span className="principal-pill d-flex align-items-center gap-2">
            <i className="bi bi-mortarboard-fill" />
            <span className="fw-semibold">Principal:</span> {principal.name || "—"}
          </span>
          <div className="spacer" />
          <div className="quick-cta d-flex flex-wrap gap-2">
            <Button size="sm" variant="success" href="/school/students" className="rounded-pill d-flex align-items-center gap-2">
              <Icon3D src={PNG_Students} size={22} alt="" /> Add Student
            </Button>
            <Button size="sm" variant="outline-light" href="/school/teachers" className="rounded-pill d-flex align-items-center gap-2">
              <Icon3D src={PNG_Teachers} size={22} alt="" /> Teachers
            </Button>
            <Button size="sm" variant="outline-light" href="/school/assignments" className="rounded-pill d-flex align-items-center gap-2">
              <Icon3D src={PNG_Assignments} size={22} alt="" /> Assignment
            </Button>
            <Button size="sm" variant="outline-light" href="/school/exams" className="rounded-pill d-flex align-items-center gap-2">
              <Icon3D src={PNG_Exams} size={22} alt="" /> Exam
            </Button>
          </div>
        </div>
      </div>

      {/* KPI GRID (3D icons) */}
      <Row className="g-3 mt-1">
        <Col xs={6} md={4} lg={2}><GlowTile title="Students"        value={counts.students}     png={PNG_Students}   delay={0}   /></Col>
        <Col xs={6} md={4} lg={2}><GlowTile title="Teachers"        value={counts.teachers}     png={PNG_Teachers}   delay={40}  /></Col>
        <Col xs={6} md={4} lg={2}><GlowTile title="Online Classes"  value={counts.onlineClasses} png={PNG_Classes}    delay={80}  /></Col>
        <Col xs={6} md={4} lg={2}><GlowTile title="Circulars"       value={counts.circulars}    png={PNG_Megaphone}  delay={120} /></Col>
        <Col xs={6} md={4} lg={2}><GlowTile title="Assignments"     value={counts.assignments}  png={PNG_Assignments} delay={160} /></Col>
        <Col xs={6} md={4} lg={2}><GlowTile title="Exams"           value={counts.exams}        png={PNG_Exams}      delay={200} /></Col>
      </Row>

      {/* Quick Apps grid (navigation) */}
      <Row className="g-3 mt-1">
        {[
          { title: "Profile",     href: "/school/profile",               png: PNG_Profile },
          { title: "Fees",        href: "/school/fees/login",            png: PNG_Fees },
          { title: "Attendance",  href: "/school/attendance",            png: PNG_Attendance },
          { title: "Results",     href: "/school/exams/studentresults",  png: PNG_Progress },
          { title: "Support",     href: "/school/support",               png: PNG_Support },
          { title: "Settings",    href: "/school/settings",              png: PNG_Settings },
        ].map((a) => (
          <Col key={a.title} xs={6} sm={4} lg={2}>
            <Card as="a" href={a.href} className="nav-card text-decoration-none h-100">
              <Card.Body className="d-flex flex-column align-items-center justify-content-center gap-2">
                <Icon3D src={a.png} size={48} alt={a.title} />
                <div className="fw-semibold text-center">{a.title}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* PINNED INSIGHTS (Charts) */}
      <PinSection height={660} offsetTop={72}>
        <Row className="g-3 mt-1">
          <Col lg={6}>
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .5 }}>
              <Card className="border-0 glass-strong h-100">
                <Card.Header className="bg-transparent fw-semibold d-flex justify-content-between align-items-center">
                  <span>Students by Class</span>
                  <Badge bg="light" text="dark" className="border">{studentsByClass.length} groups</Badge>
                </Card.Header>
                <Card.Body style={{ height: 360 }}>
                  {studentsByClass.length === 0 ? (
                    <div className="text-center text-muted py-5">No data</div>
                  ) : (
                    <ResponsiveContainer>
                      <BarChart data={studentsByClass} margin={{ top: 8, right: 8, left: 0, bottom: 16 }}>
                        <CartesianGrid stroke="var(--grid-color, rgba(0,0,0,.06))" strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fill: vars.muted }} interval={0} angle={-20} textAnchor="end" height={64} />
                        <YAxis allowDecimals={false} tick={{ fill: vars.muted }} />
                        <RTooltip contentStyle={{ background: vars.bodyBg, borderColor: vars.border, color: vars.bodyColor }} />
                        <Bar dataKey="value" fill="var(--accent)" radius={[10,10,6,6]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card.Body>
              </Card>
            </motion.div>
          </Col>

          <Col lg={6}>
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: .5, delay: .08 }}>
              <Card className="border-0 glass-strong h-100">
                <Card.Header className="bg-transparent fw-semibold d-flex align-items-center justify-content-between">
                  <span>Activity (Last 14 days)</span>
                  <div className="btn-group btn-group-sm">
                    <Button variant={activityFocus==="all" ? "success" : "outline-secondary"} onClick={()=>setActivityFocus("all")}>All</Button>
                    <Button variant={activityFocus==="circulars" ? "success" : "outline-secondary"} onClick={()=>setActivityFocus("circulars")}>Circulars</Button>
                    <Button variant={activityFocus==="exams" ? "success" : "outline-secondary"} onClick={()=>setActivityFocus("exams")}>Exams</Button>
                    <Button variant={activityFocus==="assignments" ? "success" : "outline-secondary"} onClick={()=>setActivityFocus("assignments")}>Assignments</Button>
                  </div>
                </Card.Header>
                <Card.Body style={{ height: 360 }}>
                  {activity.length === 0 ? (
                    <div className="text-center text-muted py-5">No data</div>
                  ) : (
                    <ResponsiveContainer>
                      <AreaChart data={activity} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                        <defs>
                          <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={accent} stopOpacity={0.5}/>
                            <stop offset="95%" stopColor={accent} stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={vars.warning} stopOpacity={0.45}/>
                            <stop offset="95%" stopColor={vars.warning} stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="g3" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={vars.success} stopOpacity={0.45}/>
                            <stop offset="95%" stopColor={vars.success} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="var(--grid-color, rgba(0,0,0,.06))" strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fill: vars.muted }} />
                        <YAxis allowDecimals={false} tick={{ fill: vars.muted }} />
                        <RTooltip contentStyle={{ background: vars.bodyBg, borderColor: vars.border, color: vars.bodyColor }} />
                        {filterKeys.includes("circulars") && <Area type="monotone" dataKey="circulars" stroke={accent} fill="url(#g1)" />}
                        {filterKeys.includes("exams") && <Area type="monotone" dataKey="exams" stroke={vars.warning} fill="url(#g2)" />}
                        {filterKeys.includes("assignments") && <Area type="monotone" dataKey="assignments" stroke={vars.success} fill="url(#g3)" />}
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </PinSection>

      {/* RECENT LISTS (virtualized + animated) */}
      <Row className="g-3 mt-1">
        <Col lg={4}>
          <Card className="border-0 glass h-100">
            <Card.Header className="bg-transparent fw-semibold d-flex justify-content-between align-items-center">
              <span className="d-flex align-items-center gap-2">
                <Icon3D src={PNG_Megaphone} size={22} alt="" /> Latest Circulars
              </span>
              <Button size="sm" variant="outline-secondary" href="/school/circulars">All</Button>
            </Card.Header>
            <Card.Body className="p-0">
              <VirtualList
                items={recent.circulars}
                emptyText="No circulars"
                renderRow={(c) => {
                  const when = fmtDate(toDate(c.publishAt) || toDate(c.createdAt), "DD MMM, HH:mm");
                  return (
                    <div className="d-flex w-100 align-items-center justify-content-between gap-2">
                      <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: 260 }}>
                        <span className="dot" />
                        <span className="fw-semibold text-truncate">{c.title || "Untitled"}</span>
                      </div>
                      <div className="text-muted small ms-2">{when}</div>
                    </div>
                  );
                }}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 glass h-100">
            <Card.Header className="bg-transparent fw-semibold d-flex justify-content-between align-items-center">
              <span className="d-flex align-items-center gap-2">
                <Icon3D src={PNG_Exams} size={22} alt="" /> Upcoming Exams
              </span>
              <Button size="sm" variant="outline-secondary" href="/school/exams">All</Button>
            </Card.Header>
            <Card.Body className="p-0">
              <VirtualList
                items={recent.exams}
                emptyText="No upcoming exams"
                renderRow={(e) => {
                  const d = toDate(e.date);
                  return (
                    <div className="d-flex w-100 align-items-center justify-content-between gap-2">
                      <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: 260 }}>
                        <span className="dot" />
                        <span className="fw-semibold text-truncate">{e.name || e.subject || "Exam"}</span>
                      </div>
                      <div className="text-muted small ms-2">{fmtDate(d)}</div>
                    </div>
                  );
                }}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 glass h-100">
            <Card.Header className="bg-transparent fw-semibold d-flex justify-content-between align-items-center">
              <span className="d-flex align-items-center gap-2">
                <Icon3D src={PNG_Assignments} size={22} alt="" /> Assignments Due Soon
              </span>
              <Button size="sm" variant="outline-secondary" href="/school/assignments">All</Button>
            </Card.Header>
            <Card.Body className="p-0">
              <VirtualList
                items={recent.assignments}
                emptyText="No upcoming deadlines"
                renderRow={(a) => {
                  const due = fmtDate(toDate(a.dueAt), "DD MMM, HH:mm");
                  return (
                    <div className="d-flex w-100 align-items-center justify-content-between gap-2">
                      <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: 260 }}>
                        <span className="dot" />
                        <span className="fw-semibold text-truncate">{a.title || "Assignment"}</span>
                      </div>
                      <div className="text-muted small ms-2">{due}</div>
                    </div>
                  );
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Floating Chat FAB (3D support icon) */}
      <div className="chat-fab">
        <motion.button
          className="btn btn-success rounded-pill d-flex align-items-center gap-2"
          aria-label="Open support chat"
          onClick={() => setChatOpen(true)}
          whileHover={{ y: -2 }}
          whileTap={{ scale: .98 }}
          transition={{ type: "spring", stiffness: 240, damping: 18 }}
        >
          <Icon3D src={PNG_Support} size={22} alt="" />
          <span className="label">Chat</span>
        </motion.button>
      </div>

      {/* Chat Offcanvas with presence */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <Offcanvas show={chatOpen} onHide={() => setChatOpen(false)} placement="end">
              <Offcanvas.Header closeButton>
                <Offcanvas.Title className="d-flex align-items-center gap-2">
                  <Icon3D src={PNG_Support} size={22} alt="" /> Support Chat
                </Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body className="d-flex flex-column gap-2">
                <div className="flex-grow-1 d-flex flex-column gap-2">
                  {messages.map(m => (
                    <motion.div
                      key={m.id}
                      className={`chat-bubble ${m.who === "me" ? "me align-self-end" : "other align-self-start"}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {m.text}
                    </motion.div>
                  ))}
                </div>
                <div className="d-flex gap-2">
                  <Form.Control
                    placeholder="Type a message…"
                    value={draftMsg}
                    onChange={(e) => setDraftMsg(e.target.value)}
                    onKeyDown={(e) => (e.key === "Enter" ? sendMsg() : null)}
                  />
                  <Button variant="success" onClick={sendMsg}><i className="bi bi-send" /></Button>
                </div>
              </Offcanvas.Body>
            </Offcanvas>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Styles (theme-aware + responsive) */}
      <style>{`
        :root {
          --accent: ${accent};
          --accent-04: ${withAlpha(accent, .04)};
          --accent-10: ${withAlpha(accent, .10)};
          --accent-18: ${withAlpha(accent, .18)};
          --accent-30: ${withAlpha(accent, .30)};
          --grid-color: ${withAlpha(vars.bodyColor, .08)};
        }
        [data-bs-theme="dark"] { --grid-color: ${withAlpha("#ffffff", .08)}; }

        /* -------- Hero Banner (dark/light) -------- */
        .ribbon { border-radius: 16px; overflow: hidden; margin-bottom: 14px; }
        .hero-banner{
          --hero-fg: #ffffff;
          --hero-fg-dim: color-mix(in oklab, var(--hero-fg) 70%, transparent);
          --hero-border: color-mix(in oklab, var(--bs-body-color) 8%, transparent);
          --hero-film: color-mix(in oklab, var(--hero-fg) 10%, transparent);
          --icon3d-shadow: 0 10px 20px rgba(0,0,0,.20);
          --icon3d-shadow-dark: 0 14px 26px rgba(0,0,0,.45);
          position: relative; border: 1px solid var(--hero-border);
          background:
            radial-gradient(1200px 300px at 80% -120%, color-mix(in oklab, var(--accent) 25%, var(--bs-body-bg) 75%), transparent 70%),
            linear-gradient(135deg, color-mix(in oklab, var(--accent) 70%, #7c3aed 30%), #8b5cf6);
          box-shadow: 0 18px 40px rgba(0,0,0,.16);
          color: var(--hero-fg);
        }
        [data-bs-theme="dark"] .hero-banner{
          --hero-fg: #e9e9f1;
          background:
            radial-gradient(1200px 300px at 80% -120%, color-mix(in oklab, var(--accent) 25%, #111 75%), transparent 70%),
            linear-gradient(135deg, color-mix(in oklab, var(--accent) 60%, #5b21b6 40%), #4c1d95);
          border-color: color-mix(in oklab, #fff 8%, transparent);
          box-shadow: 0 16px 36px rgba(0,0,0,.5);
        }
        .hero-bg{ position: relative; height: 12px; background: linear-gradient(180deg, var(--hero-film), transparent); }

        /* Art area (never spills) */
    
       

        .bubble{ position:absolute; border-radius:999px; background:#fff; opacity:.55; filter: blur(.5px); width:10px; height:10px; }
        .bubble.b1{ left: 12%; top: -8px; }
        .bubble.b2{ left: 48%; top: -10px; width:8px; height:8px; }
        .bubble.b3{ left: 66%; top: -6px; width:12px; height:12px; }

        .hero-content{ display: grid; grid-template-columns: 1fr auto; gap: 18px; padding: 18px; }
        .hero-date{ color: var(--hero-fg-dim); }
        .hero-greet{ color: var(--hero-fg); line-height: 1.1; }
        .hero-sub{ color: var(--hero-fg-dim); }
        .hero-meta{ color: var(--hero-fg-dim); }
        .chip-row .chip{
          color: var(--hero-fg);
          border: 1px solid color-mix(in oklab, var(--hero-fg) 22%, transparent);
          background: color-mix(in oklab, var(--hero-fg) 10%, transparent);
          border-radius: 999px;
          padding: .28rem .66rem;
          font-weight: 600; letter-spacing: .15px;
          backdrop-filter: blur(6px);
        }

        .principal-strip.soft{
          display: grid; grid-template-columns: 1fr auto; gap: 12px;
          padding: 12px 18px 16px;
          border-top: 1px dashed color-mix(in oklab, var(--hero-fg) 20%, transparent);
          background: linear-gradient(180deg, var(--hero-film), transparent);
          color: var(--hero-fg);
        }
        .principal-pill{
          border: 1px solid color-mix(in oklab, var(--hero-fg) 22%, transparent) !important;
          padding: 8px 12px; border-radius: 999px; background: color-mix(in oklab, var(--hero-fg) 10%, transparent);
        }
        .quick-cta .btn{
          --bs-btn-color: var(--hero-fg);
          --bs-btn-border-color: color-mix(in oklab, var(--hero-fg) 30%, transparent);
          --bs-btn-hover-border-color: color-mix(in oklab, var(--hero-fg) 45%, transparent);
        }

        /* 3D PNG presentation */
        .icon3d{ display:block; object-fit:contain; image-rendering:auto; will-change: transform;
          filter: drop-shadow(0 10px 20px rgba(0,0,0,.20)); background: transparent; }
        [data-bs-theme="dark"] .icon3d{ filter: drop-shadow(0 14px 26px rgba(0,0,0,.45)); }

        @media (max-width: 576px){
          .hero-content{ grid-template-columns: 1fr; padding: 16px; }
          .principal-strip.soft{ grid-template-columns: 1fr; }
          .hero-art{ right: 6px; top: -26px; width: clamp(120px, 28vw, 180px); height: auto; opacity: .9; }
        }

        /* Cards */
        .card { border-radius: 18px; }
        .glass {
          backdrop-filter: blur(6px);
          box-shadow: 0 8px 24px rgba(0,0,0,.08);
          border: 1px solid ${withAlpha(vars.bodyColor, .06)} !important;
          background: linear-gradient(180deg, var(--accent-04), transparent 70%), var(--bs-body-bg);
        }
        .glass-strong {
          backdrop-filter: blur(10px);
          box-shadow: 0 12px 28px rgba(0,0,0,.10);
          border: 1px solid ${withAlpha(vars.bodyColor, .08)} !important;
          background:
            radial-gradient(240px 120px at 10% 0%, var(--accent-10), transparent 70%),
            radial-gradient(260px 120px at 90% 0%, var(--accent-04), transparent 70%),
            var(--bs-body-bg);
        }
        .nav-card{
          border: 1px solid ${withAlpha(vars.bodyColor, .06)} !important;
          background: linear-gradient(180deg, var(--accent-04), transparent 70%), var(--bs-body-bg);
          box-shadow: 0 6px 18px rgba(0,0,0,.06);
          border-radius: 16px;
          transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
          display:block; color: inherit;
        }
        .nav-card:hover{
          transform: translateY(-3px);
          box-shadow: 0 12px 26px rgba(0,0,0,.12);
          border-color: var(--accent-18) !important;
        }

        .hover-lift { transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease; }
        .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(16,24,40,.08); }

        /* KPI tiles */
        .glow-tile {
          background: linear-gradient(180deg, var(--accent-04), transparent 70%), var(--bs-body-bg);
          border: 1px solid ${withAlpha(vars.bodyColor, .06)} !important;
          box-shadow: 0 6px 18px rgba(0,0,0,.06);
          transform-style: preserve-3d; perspective: 800px;
        }
        .glow-tile:hover { transform: translateY(-2px) rotateX(2deg) rotateY(2deg); }
        .glow-tile .tile-icon {
          width: 48px; height: 48px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          background: var(--accent-10);
          border: 1px solid ${withAlpha(vars.bodyColor, .08)};
          color: var(--accent);
        }

        /* Lists */
        .card-header{ border-bottom: 1px solid var(--grid-color); }
        .dot{
          width: 8px; height: 8px; border-radius: 999px; background: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-10);
          flex: 0 0 auto;
        }

        /* Floating chat */
        .chat-fab { position: fixed; right: 18px; bottom: 18px; z-index: 1080; }
        .chat-fab .btn {
          padding: .65rem .9rem;
          box-shadow: 0 10px 22px rgba(16,24,40,.14);
          transition: transform .16s ease, box-shadow .16s ease, background-color .16s ease, border-color .16s ease;
        }
        .chat-fab .btn:hover { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(16,24,40,.18); }

        .chat-bubble { border-radius: 14px; padding: .55rem .75rem; max-width: 92%; box-shadow: 0 2px 10px rgba(16,24,40,.06); }
        .chat-bubble.me { background: #e9f7f2; }
        .chat-bubble.other { background: #fff; border: 1px solid var(--bs-border-color); }

.chat-bubble.other { background: #fff; border: 1px solid var(--bs-border-color); }
      `}</style>
    </>
  );
}